import { useCallback, useEffect, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent, RefObject } from 'react';

/**
 * Přetahování bloků v editoru šablon přes pointer události (myš, dotyk, pero).
 *
 * Nativní HTML5 drag & drop se na dotykových zařízeních nespustí vůbec, ve Firefoxu
 * potřebuje `dataTransfer.setData` a neumí přetáhnout položku z nabídky mezi bloky
 * se zvýrazněným místem vložení. Tohle řešení počítá místo vložení z polohy ukazatele
 * a vykresluje ho stejně pro všechny vstupy.
 */
export type DragSource =
  { kind: 'move'; index: number; label: string } | { kind: 'new'; type: string; label: string };

interface Options {
  listRef: RefObject<HTMLElement>;
  enabled: boolean;
  onDrop: (source: DragSource, at: number) => void;
}

const MOVE_THRESHOLD = 5;
const LONG_PRESS_MS = 350;

export function useBlockDnd({ listRef, enabled, onDrop }: Options): {
  source: DragSource | null;
  dropAt: number | null;
  ghostRef: RefObject<HTMLDivElement>;
  begin: (event: ReactPointerEvent, source: DragSource, opts?: { immediate?: boolean }) => void;
  consumeClick: () => boolean;
} {
  const [source, setSource] = useState<DragSource | null>(null);
  const [dropAt, setDropAt] = useState<number | null>(null);
  const ghostRef = useRef<HTMLDivElement>(null);
  const clickBlocked = useRef(false);
  const cleanup = useRef<(() => void) | null>(null);
  const dropRef = useRef(onDrop);
  dropRef.current = onDrop;

  useEffect(() => () => cleanup.current?.(), []);

  const begin = useCallback(
    (event: ReactPointerEvent, src: DragSource, opts?: { immediate?: boolean }) => {
      if (!enabled || (event.pointerType === 'mouse' && event.button !== 0)) return;
      cleanup.current?.();
      const startX = event.clientX;
      const startY = event.clientY;
      const touch = event.pointerType === 'touch';
      let dragging = false;
      let at: number | null = null;
      const timer: { id?: number } = {};

      const place = (x: number, y: number): void => {
        ghostRef.current?.style.setProperty('transform', `translate(${x + 14}px, ${y + 14}px)`);
      };

      const locate = (x: number, y: number): number | null => {
        const list = listRef.current;
        if (!list) return null;
        const r = list.getBoundingClientRect();
        if (x < r.left - 32 || x > r.right + 32 || y < r.top - 48 || y > r.bottom + 64) return null;
        const items = Array.from(list.querySelectorAll<HTMLElement>(':scope > li.blk'));
        let index = 0;
        for (const item of items) {
          const b = item.getBoundingClientRect();
          if (y > b.top + b.height / 2) index += 1;
        }
        return index;
      };

      const start = (): void => {
        dragging = true;
        setSource(src);
        document.body.classList.add('s2b-dragging');
        place(startX, startY);
      };

      const move = (e: PointerEvent): void => {
        if (!dragging) {
          if (Math.hypot(e.clientX - startX, e.clientY - startY) < MOVE_THRESHOLD) return;
          if (touch && !opts?.immediate) {
            // Prst se pohnul dřív než po dlouhém stisku: člověk posouvá stránku.
            finish(false);
            return;
          }
          start();
        }
        place(e.clientX, e.clientY);
        at = locate(e.clientX, e.clientY);
        setDropAt(at);
        // Posun stránky, když je ukazatel u okraje okna.
        if (e.clientY < 70) window.scrollBy(0, -14);
        else if (e.clientY > window.innerHeight - 70) window.scrollBy(0, 14);
      };

      const finish = (commit: boolean): void => {
        window.clearTimeout(timer.id);
        document.removeEventListener('pointermove', move);
        document.removeEventListener('pointerup', up);
        document.removeEventListener('pointercancel', cancel);
        document.removeEventListener('keydown', key);
        document.removeEventListener('touchmove', block);
        document.body.classList.remove('s2b-dragging');
        cleanup.current = null;
        if (dragging) {
          clickBlocked.current = true;
          window.setTimeout(() => {
            clickBlocked.current = false;
          }, 0);
          if (commit && at !== null) dropRef.current(src, at);
        }
        setSource(null);
        setDropAt(null);
      };
      const up = (): void => finish(true);
      const cancel = (): void => finish(false);
      const key = (e: KeyboardEvent): void => {
        if (e.key === 'Escape') finish(false);
      };
      // Během tažení prstem se stránka nesmí posouvat.
      const block = (e: TouchEvent): void => {
        if (dragging) e.preventDefault();
      };

      document.addEventListener('pointermove', move);
      document.addEventListener('pointerup', up);
      document.addEventListener('pointercancel', cancel);
      document.addEventListener('keydown', key);
      document.addEventListener('touchmove', block, { passive: false });
      cleanup.current = () => finish(false);

      if (opts?.immediate || !touch) return;
      timer.id = window.setTimeout(() => {
        start();
        navigator.vibrate?.(15);
      }, LONG_PRESS_MS);
    },
    [enabled, listRef],
  );

  /** Po tažení nesmí následovat „klik“, který by blok otevřel nebo přidal podruhé. */
  const consumeClick = useCallback(() => clickBlocked.current, []);

  return { source, dropAt, ghostRef, begin, consumeClick };
}
